import React, { useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkle, Copy, DeviceMobile, EnvelopeSimple } from "@phosphor-icons/react";

const TONE_OPTIONS = [
  { value: "warm", label: "Warm & friendly" },
  { value: "concise", label: "Concise & professional" },
  { value: "salesy", label: "Confident & persuasive" },
  { value: "apologetic", label: "Empathetic & apologetic" },
];

const MODEL_LABELS = {
  claude: "Claude",
  gemini: "Gemini",
  openai: "ChatGPT",
};

// Shows 3 side-by-side AI-generated reply drafts (Claude / Gemini / ChatGPT)
// pulled from the lead's message + full VA activity log. VA picks whichever
// draft they like, copies it, or fires it as an SMS/email via the existing
// send hook if the parent passes one.
export default function AiReplyDrafter({ leadId, phone, onSendSms, onUseAsEmail }) {
  const [tone, setTone] = useState("warm");
  const [channel, setChannel] = useState("sms");
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState(null);

  const generate = async () => {
    if (!leadId) return;
    setLoading(true);
    setDrafts(null);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/ai-replies`, { channel, tone });
      setDrafts(data.drafts || []);
    } catch (e) {
      toast.error(getErr(e));
    }
    setLoading(false);
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste anywhere.");
    } catch {
      toast.error("Couldn't copy — long-press and copy manually.");
    }
  };

  const sendSms = (text) => {
    if (onSendSms) return onSendSms(text);
    if (phone) {
      const href = `sms:${phone}?body=${encodeURIComponent(text)}`;
      window.location.href = href;
    } else {
      toast.error("No phone on file — copy the message and send manually.");
    }
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-4" data-testid="ai-reply-drafter">
      <div className="flex items-center gap-2">
        <Sparkle size={16} weight="fill" className="text-[#0044FF]" />
        <div className="font-display text-sm font-black tracking-tight">AI quick reply drafts</div>
      </div>
      <p className="mt-1 text-[11px] text-[#4B5563]">
        Reads this lead&apos;s message and every VA activity note, then asks Claude, Gemini, and ChatGPT
        to draft the same next {channel === "sms" ? "text" : "email"} — pick whichever sounds best.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] font-bold text-[#4B5563]">
          Tone:
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="border border-[#E5E7EB] bg-white px-2 py-1 text-xs font-semibold text-[#030712]"
            data-testid="ai-reply-tone"
          >
            {TONE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="flex overflow-hidden border border-[#E5E7EB]">
          <button
            type="button"
            className={`px-2 py-1 text-[11px] font-bold ${channel === "sms" ? "bg-[#030712] text-white" : "bg-white text-[#4B5563]"}`}
            onClick={() => setChannel("sms")}
            data-testid="ai-reply-channel-sms"
          >
            <DeviceMobile size={12} className="mr-1 inline" />SMS
          </button>
          <button
            type="button"
            className={`px-2 py-1 text-[11px] font-bold ${channel === "email" ? "bg-[#030712] text-white" : "bg-white text-[#4B5563]"}`}
            onClick={() => setChannel("email")}
            data-testid="ai-reply-channel-email"
          >
            <EnvelopeSimple size={12} className="mr-1 inline" />Email
          </button>
        </div>
        <Button
          size="sm"
          onClick={generate}
          disabled={loading}
          data-testid="ai-reply-generate"
        >
          <Sparkle size={12} weight="fill" /> {loading ? "Drafting…" : (drafts ? "Regenerate" : "Draft with AI")}
        </Button>
      </div>

      {loading && (
        <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="ai-reply-loading">
          {["claude", "gemini", "openai"].map((k) => (
            <div key={k} className="border border-dashed border-[#E5E7EB] p-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-[#4B5563]">{MODEL_LABELS[k]}</div>
              <div className="mt-2 h-3 w-full animate-pulse bg-[#F3F4F6]" />
              <div className="mt-1 h-3 w-11/12 animate-pulse bg-[#F3F4F6]" />
              <div className="mt-1 h-3 w-3/4 animate-pulse bg-[#F3F4F6]" />
            </div>
          ))}
        </div>
      )}

      {!loading && drafts && (
        <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="ai-reply-drafts">
          {drafts.map((d) => (
            <div
              key={d.label}
              className="flex flex-col border border-[#E5E7EB] p-3"
              data-testid={`ai-reply-draft-${d.label}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-wider text-[#0044FF]">
                  {MODEL_LABELS[d.label] || d.label}
                </div>
                <div className="text-[9px] text-[#9CA3AF]">{d.model}</div>
              </div>
              {d.ok ? (
                <>
                  <div
                    className="mt-2 flex-1 whitespace-pre-wrap text-[13px] leading-snug text-[#030712]"
                    data-testid={`ai-reply-text-${d.label}`}
                  >
                    {d.text}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    <button
                      className="border border-[#E5E7EB] px-2 py-1 text-[11px] font-bold hover:bg-[#F9FAFB]"
                      onClick={() => copyText(d.text)}
                      data-testid={`ai-reply-copy-${d.label}`}
                    >
                      <Copy size={11} className="mr-1 inline" />Copy
                    </button>
                    {channel === "sms" && (
                      <button
                        className="border border-[#030712] bg-[#030712] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#1F2937]"
                        onClick={() => sendSms(d.text)}
                        data-testid={`ai-reply-send-${d.label}`}
                      >
                        <DeviceMobile size={11} className="mr-1 inline" />Send SMS
                      </button>
                    )}
                    {channel === "email" && onUseAsEmail && (
                      <button
                        className="border border-[#030712] bg-[#030712] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#1F2937]"
                        onClick={() => onUseAsEmail(d.text)}
                        data-testid={`ai-reply-use-email-${d.label}`}
                      >
                        <EnvelopeSimple size={11} className="mr-1 inline" />Use as email
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-2 text-[11px] text-[#B91C1C]" data-testid={`ai-reply-error-${d.label}`}>
                  Draft unavailable — {d.error || "provider error"}.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && !drafts && (
        <div className="mt-3 text-[11px] text-[#4B5563]" data-testid="ai-reply-empty">
          Tap <b>Draft with AI</b> to see three model suggestions side by side.
        </div>
      )}
    </div>
  );
}
